import { CopyButton } from '@heidi/design-system/copy-button';
import { toast } from '@heidi/design-system/toast';
import { TypographyCaption } from '@heidi/design-system/typography';
import { PencilLine } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { useIntl } from 'react-intl';

import {
  DRAFT_APPROVAL_KIND,
  USER_DISCARDED_DRAFT_REASON,
  resolveDraftPresentation,
  type DraftPresentation,
  type DraftResolution,
} from '../../contracts/approval-kinds';
import type { ApprovalResolution, ApprovalVerdict } from '../../contracts/harness.types';
import { sharedAgentMessages } from '../../shared.messages';
import { ShimmerText } from '../projects/timeline/ShimmerText';
import { DraftBody, type DraftApprovalBodyRenderArgs } from './draft-body';
import { formatDraftHeader, resolveDraftHeaderSpec } from './draft-header';
import { InlineApprovalActions } from './inline-approval-actions';
import type { TextEditPreview } from './text-edit-preview';
import { useDraftPreview } from './use-draft-preview';

export type ResolveDraftApproval = (
  approvalId: string,
  verdict: ApprovalVerdict,
  reason?: string,
  modifiedInput?: Record<string, unknown> | null,
  resolution?: ApprovalResolution
) => Promise<void>;

export interface DraftApprovalBlockProps {
  toolName: string;
  /**
   * The approval's kind once its record has loaded. Until then the card shows
   * by tool name, and the approval may turn out not to be a draft.
   */
  approvalKind?: string;
  toolInput: Record<string, unknown>;
  partialInput?: string;
  status: 'streaming_input' | 'pending_approval';
  approvalId?: string;
  draft?: DraftPresentation | null;
  beforeText?: string;
  /** The backend's before/after for this edit; wins over `beforeText`. */
  serverPreview?: TextEditPreview | null;
  onResolve?: ResolveDraftApproval;
  /** Host-supplied note preview (TipTap). Falls back to preformatted text. */
  renderBody?: (args: DraftApprovalBodyRenderArgs) => ReactNode;
  /** The edited document's name, when the tool input doesn't carry it. */
  documentTitle?: string;
}

export const DraftApprovalBlock = ({
  toolName,
  toolInput,
  partialInput,
  status,
  approvalId,
  approvalKind,
  draft: draftProp,
  beforeText,
  serverPreview,
  onResolve,
  renderBody,
  documentTitle,
}: DraftApprovalBlockProps) => {
  const intl = useIntl();
  const draft = draftProp ?? resolveDraftPresentation(toolName);
  const isStreaming = status === 'streaming_input';
  const [pendingVerdict, setPendingVerdict] = useState<ApprovalVerdict | null>(null);

  const { displayBody, tintAgainst, removesOnly, copyText, copiesReplacement } =
    useDraftPreview({
      toolInput,
      partialInput,
      draft,
      beforeText,
      serverPreview,
      isStreaming,
    });

  const canAct = !isStreaming && Boolean(approvalId) && Boolean(onResolve);
  const submit = (verdict: ApprovalVerdict) => {
    if (!approvalId || !onResolve || pendingVerdict !== null) return;
    setPendingVerdict(verdict);
    // A draft resolution is valid only on a draft approval; the backend rejects
    // it on any other kind. The older token goes with every discard: a backend
    // without the resolution (an older region, or a 422 retry without it)
    // still reads it as a discard, and one with it ignores the token.
    const isDraftApproval = approvalKind === DRAFT_APPROVAL_KIND;
    const isDiscard = verdict === 'deny';
    const resolution: DraftResolution | undefined =
      isDiscard && isDraftApproval ? { kind: 'draft.discarded' } : undefined;
    const reason = isDiscard ? USER_DISCARDED_DRAFT_REASON : undefined;
    // In-card rewrite is parked. Approve commits the original tool input.
    // `modified_input` stays on the resolve type if we bring edit back.
    const payload = verdict === 'approve' ? undefined : null;
    void onResolve(approvalId, verdict, reason, payload, resolution).catch(
      (error: unknown) => {
        setPendingVerdict(null);
        toast({
          title: intl.formatMessage(sharedAgentMessages.couldntResolve),
          description:
            error instanceof Error
              ? error.message
              : intl.formatMessage({
                  defaultMessage: 'Unknown error',
                  id: '7POY7QQrzT',
                  description: 'Toast fallback when an approval fails without details',
                }),
          tone: 'destructive',
        });
      }
    );
  };

  const handleCopy = async () => {
    const textToCopy = copyText;
    if (!textToCopy) return false;
    try {
      await navigator.clipboard.writeText(textToCopy);
      return true;
    } catch {
      toast({
        title: intl.formatMessage({
          defaultMessage: "Couldn't copy",
          id: '7Z43e8o/yX',
        }),
        tone: 'destructive',
      });
      return false;
    }
  };

  const headerLabel = formatDraftHeader(
    intl,
    resolveDraftHeaderSpec(status, draft, toolInput, { toolName, documentTitle })
  );

  return (
    <div
      className="my-4 rounded-xl border border-dashed border-border bg-surface px-4 py-3 first:mt-0"
      data-testid="draft-approval-block"
    >
      <div className="mb-3 flex items-center gap-1.5">
        <PencilLine className="size-3 shrink-0 text-text-tertiary" />
        <TypographyCaption className="min-w-0 truncate text-text-tertiary">
          {isStreaming ? <ShimmerText>{headerLabel}</ShimmerText> : headerLabel}
        </TypographyCaption>
      </div>

      {displayBody ? (
        <DraftBody
          body={displayBody}
          isStreaming={isStreaming}
          tintAgainst={tintAgainst}
          {...(removesOnly ? { removal: true } : {})}
          renderBody={renderBody}
        />
      ) : null}

      {canAct ? (
        <div className="mt-6 flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-1">
            {copyText ? (
              <CopyButton
                onCopy={handleCopy}
                variant="ghost"
                size="sm"
                className="text-text-secondary"
                label={
                  copiesReplacement
                    ? intl.formatMessage({
                        defaultMessage: 'Copy replacement',
                        id: 'dUYPrVi++Y',
                      })
                    : intl.formatMessage({ defaultMessage: 'Copy', id: '4l6vz1/eZ5' })
                }
                copiedLabel={intl.formatMessage(sharedAgentMessages.copied)}
                data-testid="draft-approval-copy"
              />
            ) : null}
          </div>
          <InlineApprovalActions
            onApprove={() => submit('approve')}
            onDeny={() => submit('deny')}
            pendingVerdict={pendingVerdict}
            denyLabel={intl.formatMessage({
              defaultMessage: 'Discard',
              id: 'nmpevlUATU',
            })}
            approveTestId="draft-approval-apply"
            denyTestId="draft-approval-discard"
          />
        </div>
      ) : null}
    </div>
  );
};
